import type {
  AdminUserSummary,
  CreateUserInput,
  Locale,
  PermissionCode,
  SessionUser,
} from '@kjotkompanid/contracts';

const csrfToken = (): string | undefined =>
  document.cookie
    .split('; ')
    .find((entry) => entry.startsWith('kjot.csrf='))
    ?.split('=')[1];

export class ApiError extends Error {
  public constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly correlationId: string | null = null,
  ) {
    super(code);
  }
}

export const request = async <T extends object>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const method = init.method ?? 'GET';
  const csrf = csrfToken();
  const response = await fetch(`/api/${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && csrf
        ? { 'X-CSRF-Token': decodeURIComponent(csrf) }
        : {}),
      ...init.headers,
    },
  });
  const body = (await response.json()) as T | { error?: { code?: string; correlationId?: string } };
  if (!response.ok) {
    throw new ApiError(
      'error' in body ? (body.error?.code ?? 'INTERNAL_ERROR') : 'INTERNAL_ERROR',
      response.status,
      'error' in body ? (body.error?.correlationId ?? null) : null,
    );
  }
  return body as T;
};

export const api = {
  session: () => request<{ user: SessionUser }>('auth/session'),
  requestOtp: (email: string) =>
    request<{ accepted: true }>('auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyOtp: (email: string, code: string) =>
    request<{ user: SessionUser }>('auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),
  logout: () => request<{ revoked: true }>('auth/logout', { method: 'POST' }),
  updateLocale: (locale: Locale) =>
    request<{ locale: Locale }>('me/locale', { method: 'PATCH', body: JSON.stringify({ locale }) }),
  users: () => request<AdminUserSummary[]>('admin/users'),
  createUser: (input: CreateUserInput) =>
    request<{ id: string; email: string; replayed: boolean }>('admin/users', {
      method: 'POST',
      headers: { 'X-Idempotency-Key': crypto.randomUUID() },
      body: JSON.stringify(input),
    }),
  setUserStatus: (userId: string, status: 'ACTIVE' | 'DEACTIVATED') =>
    request<{ updated: true }>(`admin/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  setUserRole: (userId: string, role: 'ELDHUS_MANAGER' | 'SALESMAN' | 'VIEWER') =>
    request<{ updated: true }>(`admin/users/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  setPermissionOverride: (
    userId: string,
    permission: PermissionCode,
    effect: 'ALLOW' | 'DENY' | 'CLEAR',
  ) =>
    request<{ updated: true }>(`admin/users/${userId}/permissions/${permission}`, {
      method: 'PUT',
      body: JSON.stringify({ effect }),
    }),
  revokeAllUserSessions: (userId: string) =>
    request<{ revoked: true }>(`admin/users/${userId}/sessions/revoke-all`, { method: 'POST' }),
};
